import { TreeRow } from '../../../containers/BulkSheetView/model'
import {
  AgGridTreeHelper,
  getChildren,
  getParent,
} from '../../../containers/BulkSheetView/lib/tree'
import {
  ProgramProjectReportDto,
  ProgressValueType,
  SaveProgressAddEntry,
  SaveProgressEditEntry,
  SaveProgressKeyEntry,
  SaveProgramProjectReportRequest,
} from '../../../../infrastructure/api/program-project-reports'
import {
  IItemDelta,
  createDelta,
} from '../../../../domain/value-object/ItemDeltaInputVO'
import { AggregateField } from '../../../../domain/entity/WbsItemEntity'

export const PROGRAM_PROJECT_REPORT_ROW_TYPE = {
  PROJECT: 'PROJECT',
  WORKGROUP: 'WORKGROUP',
  PROCESS: 'PROCESS',
} as const

export type ProgramProjectReportRowType =
  (typeof PROGRAM_PROJECT_REPORT_ROW_TYPE)[keyof typeof PROGRAM_PROJECT_REPORT_ROW_TYPE]

export type ProgramProjectReportProgress = {
  total: number | null
  scheduledCompleted: number | null
  completed: number | null
  delayed: number | null
  actualResource: number | null
}

export type ProgramProjectReportResource = {
  totalResource: number | null
  investedResource: number | null
  actualResource: number | null
}

export type ProgramProjectReportProfitLoss = {
  totalBudget: number | null
  scheduledUsedBudget: number | null
  actualUsedBudget: number | null
  totalRevenue: number | null
  scheduledRevenue: number | null
  actualRevenue: number | null
  forecastRevenue: number | null
}

export type ProgramProjectReportRow = TreeRow & {
  type: ProgramProjectReportRowType
  code: string | null
  displayName: string | null
  iconUrl: string | null
  status: string | null
  scheduledStartDate: string | null
  scheduledEndDate: string | null
  progress: ProgramProjectReportProgress | null
  resource: ProgramProjectReportResource | null
  profitLoss: ProgramProjectReportProfitLoss | null
}

export const programProjectReportRowService = {
  isProject: (row?: ProgramProjectReportRow | null): boolean =>
    row?.type === PROGRAM_PROJECT_REPORT_ROW_TYPE.PROJECT,
}

export const PROGRAM_PROJECT_REPORT_INPUT_MODE = {
  FREE: 'FREE',
  AGGREGATION: 'AGGREGATION',
} as const

export type ProgramProjectReportInputMode =
  (typeof PROGRAM_PROJECT_REPORT_INPUT_MODE)[keyof typeof PROGRAM_PROJECT_REPORT_INPUT_MODE]

export const deriveSavedAggregateField = (
  root: ProgramProjectReportDto
): AggregateField | null => {
  const visit = (dto: ProgramProjectReportDto): AggregateField | null => {
    if (dto.progress?.count != null) return AggregateField.WBS_ITEM_COUNT
    if (dto.progress?.hour != null) return AggregateField.WBS_ITEM_WORKLOAD
    for (const child of dto.children ?? []) {
      const found = visit(child)
      if (found != null) return found
    }
    return null
  }
  return visit(root)
}

const toRow =
  (aggregateField: AggregateField) =>
  (dto: ProgramProjectReportDto): ProgramProjectReportRow => ({
    uuid: dto.uuid,
    treeValue: [],
    type: dto.type,
    code: dto.code,
    displayName: dto.displayName,
    iconUrl: dto.iconUrl,
    status: dto.status,
    scheduledStartDate: dto.scheduledDate?.startDate ?? null,
    scheduledEndDate: dto.scheduledDate?.endDate ?? null,
    progress:
      (aggregateField === AggregateField.WBS_ITEM_COUNT
        ? dto.progress?.count
        : dto.progress?.hour) ?? null,
    resource: dto.resource,
    profitLoss: dto.profitLoss,
    revision: dto.revision ?? undefined,
    createdBy: dto.createdBy ?? undefined,
    createdAt: dto.createdAt ?? undefined,
    updatedBy: dto.updatedBy ?? undefined,
    updatedAt: dto.updatedAt ?? undefined,
  })

export const toProgramProjectReportRows = (
  root: ProgramProjectReportDto,
  aggregateField: AggregateField
): ProgramProjectReportRow[] =>
  AgGridTreeHelper.convert(root, toRow(aggregateField))

export const emptyProgress = (): ProgramProjectReportProgress => ({
  total: null,
  scheduledCompleted: null,
  completed: null,
  delayed: null,
  actualResource: null,
})

const PROGRESS_FIELDS = [
  'total',
  'scheduledCompleted',
  'completed',
  'delayed',
  'actualResource',
] as const satisfies readonly (keyof ProgramProjectReportProgress)[]

export const sumProgress = (
  rows: ProgramProjectReportRow[]
): ProgramProjectReportProgress => {
  const result = emptyProgress()
  for (const field of PROGRESS_FIELDS) {
    let sum: number | null = null
    for (const row of rows) {
      const value = row.progress?.[field]
      if (value != null) sum = (sum ?? 0) + value
    }
    result[field] = sum
  }
  return result
}

export const aggregateProgressBottomUp = (
  rows: ProgramProjectReportRow[]
): void => {
  const deepestFirst = [...rows].sort(
    (a, b) => b.treeValue.length - a.treeValue.length
  )
  for (const row of deepestFirst) {
    const children = getChildren(rows, row)
    if (children.length === 0) continue
    row.progress = sumProgress(children)
  }
}

export const recomputeAncestorsProgress = (
  rows: ProgramProjectReportRow[],
  row: ProgramProjectReportRow,
  onRecomputed?: (row: ProgramProjectReportRow) => void
): void => {
  let parent = getParent(rows, row)
  while (parent) {
    parent.progress = sumProgress(getChildren(rows, parent))
    onRecomputed?.(parent)
    parent = getParent(rows, parent)
  }
}

export const isProgressConsistent = (
  rows: ProgramProjectReportRow[],
  row: ProgramProjectReportRow,
  field: keyof ProgramProjectReportProgress
): boolean => {
  const children = getChildren(rows, row)
  if (children.length === 0) return true
  const expected = sumProgress(children)
  return (row.progress?.[field] ?? null) === (expected[field] ?? null)
}

const RESOURCE_FIELDS = [
  'totalResource',
  'investedResource',
  'actualResource',
] as const satisfies readonly (keyof ProgramProjectReportResource)[]

const PROFIT_LOSS_FIELDS = [
  'totalBudget',
  'scheduledUsedBudget',
  'actualUsedBudget',
  'totalRevenue',
  'scheduledRevenue',
  'actualRevenue',
  'forecastRevenue',
] as const satisfies readonly (keyof ProgramProjectReportProfitLoss)[]

const hasAnyProgressValue = (
  progress: ProgramProjectReportProgress | null
): boolean =>
  !!progress && PROGRESS_FIELDS.some(field => progress[field] != null)

const toValueType = (aggregateField: AggregateField): ProgressValueType =>
  aggregateField === AggregateField.WBS_ITEM_COUNT ? 'COUNT' : 'HOUR'

const planNodeUuidOf = (row: ProgramProjectReportRow): string | null =>
  programProjectReportRowService.isProject(row) ? null : row.uuid

const isPersistableRow = (row: ProgramProjectReportRow): boolean =>
  programProjectReportRowService.isProject(row) ||
  hasAnyProgressValue(row.progress)

const buildFieldDeltas = <K extends string>(
  current: Partial<Record<K, number | null>> | null,
  original: Partial<Record<K, number | null>> | null,
  fields: readonly K[]
): Partial<Record<K, IItemDelta<number | null>>> | undefined => {
  const result: Partial<Record<K, IItemDelta<number | null>>> = {}
  for (const field of fields) {
    const delta = createDelta<number | null>(
      original?.[field] ?? null,
      current?.[field] ?? null
    )
    if (delta) result[field] = delta
  }
  return Object.keys(result).length > 0 ? result : undefined
}

const toAddEntry = (
  planNodeUuid: string | null,
  valueType: ProgressValueType,
  progress: ProgramProjectReportProgress | null
): SaveProgressAddEntry => ({
  planNodeUuid,
  valueType,
  total: progress?.total ?? null,
  scheduledCompleted: progress?.scheduledCompleted ?? null,
  completed: progress?.completed ?? null,
  delayed: progress?.delayed ?? null,
  actualResource: progress?.actualResource ?? null,
})

export const toSaveProgramProjectReportRequest = (
  rows: ProgramProjectReportRow[],
  originalRows: ProgramProjectReportRow[],
  currentAggregateField: AggregateField,
  originalAggregateField: AggregateField | null,
  options?: { clearProgress?: boolean }
): SaveProgramProjectReportRequest => {
  const clearProgress = options?.clearProgress ?? false
  const newType = toValueType(currentAggregateField)
  const oldType =
    originalAggregateField != null ? toValueType(originalAggregateField) : null

  const projectRow = rows.find(programProjectReportRowService.isProject)
  const originalProjectRow = originalRows.find(
    programProjectReportRowService.isProject
  )
  const resource = buildFieldDeltas(
    projectRow?.resource ?? null,
    originalProjectRow?.resource ?? null,
    RESOURCE_FIELDS
  )
  const profitLoss = buildFieldDeltas(
    projectRow?.profitLoss ?? null,
    originalProjectRow?.profitLoss ?? null,
    PROFIT_LOSS_FIELDS
  )

  const added: SaveProgressAddEntry[] = []
  const edited: SaveProgressEditEntry[] = []
  const deleted: SaveProgressKeyEntry[] = []

  const addAllWithValues = () => {
    for (const row of rows) {
      if (isPersistableRow(row)) {
        added.push(toAddEntry(planNodeUuidOf(row), newType, row.progress))
      }
    }
  }

  if (oldType === null) {
    addAllWithValues()
  } else if (oldType !== newType) {
    for (const orig of originalRows) {
      if (isPersistableRow(orig)) {
        deleted.push({ planNodeUuid: planNodeUuidOf(orig), valueType: oldType })
      }
    }
    if (clearProgress) {
      added.push(toAddEntry(null, newType, null))
    } else {
      addAllWithValues()
    }
  } else {
    const originalByUuid = new Map(originalRows.map(row => [row.uuid, row]))
    for (const row of rows) {
      const planNodeUuid = planNodeUuidOf(row)
      const originalProgress = originalByUuid.get(row.uuid)?.progress ?? null
      const originalRowExists = originalProgress != null
      const currentHas = hasAnyProgressValue(row.progress)
      if (!originalRowExists) {
        if (currentHas) {
          added.push(toAddEntry(planNodeUuid, newType, row.progress))
        }
      } else if (
        !currentHas &&
        !programProjectReportRowService.isProject(row)
      ) {
        deleted.push({ planNodeUuid, valueType: newType })
      } else {
        const fieldDeltas = buildFieldDeltas(
          row.progress,
          originalProgress,
          PROGRESS_FIELDS
        )
        if (fieldDeltas) {
          edited.push({ planNodeUuid, valueType: newType, ...fieldDeltas })
        }
      }
    }
  }

  return {
    ...(resource ? { resource } : {}),
    ...(profitLoss ? { profitLoss } : {}),
    progresses: { added, edited, deleted },
  }
}
